import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getRecentJournalEntries } from '../data/journalEntries';
import SEO from '../components/SEO';
import './Home.css';

const Home = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isContentVisible, setIsContentVisible] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null);

  const heroImages = [
    {
      main: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=1200&h=800&fit=crop",
      background: "/images/site-photos/BR1A0304.JPG"
    },
    {
      main: "/images/site-photos/Cover1.png",
      background: null
    },
    {
      main: "/images/gallery/value-is-still.png",
      background: null
    }
  ];

  const carouselContent = [
    {
      title: "BEAUTY. GOD. IDENTITY.",
      subtitle: "The world through my eyes.",
      buttonText: "View the Gallery",
      buttonLink: "/gallery"
    },
    {
      title: "ART IS ME",
      subtitle: "The story of how I got here and where I'm going",
      buttonText: "Read More",
      buttonLink: "/about"
    },
    {
      title: "THE VALUE IS STILL IN YOU",
      subtitle: "Discover pieces that speak to your soul",
      buttonText: "Shop Collection",
      buttonLink: "/shop"
    }
  ];

  useEffect(() => {
    // Check if device is mobile
    const isMobile = window.innerWidth <= 768;
    const intervalTime = isMobile ? 11000 : 16000; // 11 seconds on mobile, 16 seconds on desktop (cut in half)
    
    // Reset progress when slide changes
    setProgress(0);
    
    const interval = setInterval(() => {
      // Fade out content
      setIsContentVisible(false);
      
      // Change slide after fade out (synchronized with image transition)
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => 
          prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
        );
        // Fade in content
        setIsContentVisible(true);
      }, 1000); // Match the 1s text transition time
    }, intervalTime);

    return () => clearInterval(interval);
  }, [heroImages.length, currentImageIndex]);

  // Progress tracking effect
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const intervalTime = isMobile ? 11000 : 16000;
    const progressStep = 100 / (intervalTime / 100); // Progress per 100ms
    
    const progressInterval = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          return 100;
        }
        return prevProgress + progressStep;
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, [currentImageIndex]);

  const featuredWorks = [
    {
      id: 1,
      title: "The Value is Still In You",
      image: "/images/gallery/value-is-still-new.png",
      category: "Mixed Media"
    },
    {
      id: 2,
      title: "The Last of Me",
      image: "/images/gallery/hanging-on.new.png",
      category: "Mixed Media"
    },
    {
      id: 3,
      title: "John 3:30",
      image: "/images/gallery/blue-guy-new.png",
      category: "Mixed Media"
    }
  ];

  const recentJournalEntries = getRecentJournalEntries(3);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus('submitting');
    
    try {
      const requestBody = {
        email: newsletterEmail,
        listIds: [parseInt(process.env.REACT_APP_BREVO_LIST_ID)],
        updateEnabled: true,
      };
      
      const response = await fetch('https://api.brevo.com/v3/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': process.env.REACT_APP_BREVO_API_KEY,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const result = await response.json();
        setNewsletterStatus('success');
        setNewsletterEmail('');
      } else {
        const errorData = await response.json();
        console.error('Brevo API error:', errorData);
        setNewsletterStatus('error');
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      setNewsletterStatus('error');
    }
    
    setTimeout(() => {
      setNewsletterStatus(null);
    }, 5000);
  };

  return (
    <div className="home">
      <SEO 
        title="Jamelly - Beauty. God. Identity"
        description="The world through my eyes. Art pieces that represent God in my life, showing beauty and pain and joy and rain all at the same time."
        image="/images/site-photos/BR1A0304.JPG"
      />
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          {heroImages.map((imageData, index) => (
            <div key={index} className={`hero-image-container ${index === currentImageIndex ? 'active' : ''}`}>
              {imageData.background && (
                <img 
                  src={imageData.background} 
                  alt={`Hero background ${index + 1}`} 
                  className="hero-background-image"
                />
              )}
              <img 
                src={imageData.main} 
                alt={`Hero image ${index + 1}`} 
                className="hero-main-image"
              />
            </div>
          ))}
          <div className="hero-overlay"></div>
        </div>
        
        <div className={`hero-content ${isContentVisible ? 'visible' : 'fade-out'}`}>
          <div className="container">
            <h1 className="hero-title">
              {(() => {
                const title = carouselContent[currentImageIndex].title;
                const words = title.split(' ');
                const lastWord = words.pop();
                let brickClass = 'brick-font';
                if (lastWord === 'IDENTITY.') {
                  brickClass = 'brick-font identity';
                } else if (lastWord === 'YOU') {
                  brickClass = 'brick-font you';
                } else if (lastWord === 'ME') {
                  brickClass = 'brick-font me';
                }
                return (
                  <>
                    {words.join(' ')} <span className={brickClass}>{lastWord}</span>
                  </>
                );
              })()}
            </h1>
            <p className="hero-subtitle">
              {carouselContent[currentImageIndex].subtitle}
            </p>
            <Link to={carouselContent[currentImageIndex].buttonLink} className="btn btn-accent">
              {carouselContent[currentImageIndex].buttonText}
            </Link>
          </div>
        </div>

        {/* Carousel Indicators */}
        <div className="carousel-indicators">
          {heroImages.map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
              onClick={() => setCurrentImageIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
            >
              {index === currentImageIndex && (
                <div 
                  className="indicator-progress" 
                  style={{ width: `${progress}%` }}
                />
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Ticker Scroll */}
      <section className="ticker-section">
        <div className="ticker-container">
          {[...Array(15)].map((_, index) => (
            <div key={index} className="ticker-content" aria-hidden={index > 0}>
              <span className="ticker-word the">the</span>
              <span className="ticker-word world">World</span>
              <span className="ticker-word through">THROUGH</span>
              <span className="ticker-word my">MY</span>
              <span className="ticker-word eyes">EYES&nbsp;</span>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Work Carousel */}
      <section className="section featured-work">
        <div className="container">
          <h2 className="section-title text-center mb-4">
            {(() => {
              const title = "Featured Work";
              const words = title.split(' ');
              const lastWord = words.pop();
              return (
                <>
                  {words.join(' ')} <span className="brick-font">{lastWord}</span>
                </>
              );
            })()}
          </h2>
          <div className="featured-grid">
            {featuredWorks.map((work) => (
              <div key={work.id} className="featured-item">
                <div className="featured-image-container">
                  <img 
                    src={work.image} 
                    alt={work.title} 
                    className="featured-image"
                  />
                  <div className="featured-overlay">
                    <h3 className="featured-title">{work.title}</h3>
                    <p className="featured-category">{work.category}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Journal Entries */}
      <section className="section recent-journal">
        <div className="container">
          <h2 className="section-title text-center mb-4">
            {(() => {
              const title = "Recent Thoughts";
              const words = title.split(' ');
              const lastWord = words.pop();
              return (
                <>
                  {words.join(' ')} <span className="brick-font">{lastWord}</span>
                </>
              );
            })()}
          </h2>
          <div className="journal-thumbnails">
            {recentJournalEntries.map((entry) => (
              <div key={entry.id} className="journal-thumbnail">
                <div className="thumbnail-image-container">
                  <img 
                    src={entry.image} 
                    alt={entry.title} 
                    className="thumbnail-image"
                  />
                </div>
                <div className="thumbnail-content">
                  <h3 className="thumbnail-title">{entry.title}</h3>
                  <p className="thumbnail-excerpt">{entry.excerpt}</p>
                  <span className="thumbnail-date">{entry.date}</span>
                  <Link to={`/journal/${entry.id}`} className="thumbnail-link">
                    Read More
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/journal" className="btn btn-secondary">
              Read the Journal
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="section newsletter-section">
        <div className="container">
          <div className="newsletter-content">
            <h2 className="newsletter-title">Stay Connected</h2>
            <p className="newsletter-text">
              Subscribe to my newsletter for updates on new work, behind-the-scenes stories, and creative inspiration.
            </p>
            
            {newsletterStatus === 'success' && (
              <div className="success-message">
                <p>Thank you for subscribing! You'll receive updates about new work and creative inspiration.</p>
              </div>
            )}

            {newsletterStatus === 'error' && (
              <div className="error-message">
                <p>Sorry, there was an error with your subscription. Please try again.</p>
              </div>
            )}

            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="newsletter-input"
                required
              />
              <button 
                type="submit" 
                className={`btn ${newsletterStatus === 'submitting' ? 'submitting' : ''}`}
                disabled={newsletterStatus === 'submitting'}
              >
                {newsletterStatus === 'submitting' ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 